import type { FormEvent } from "react";
import { Dialog as ArkDialog } from "@ark-ui/react/dialog";
import { Portal } from "@ark-ui/react/portal";
import { Flex } from "@styled-system/jsx";
import { css, cx } from "@styled-system/css";
import { button } from "@styled-system/recipes";
import { dialogStyles } from "@theme/recipes/dialog";
import Close from "@/assets/common/close.svg?react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title?: any;
  subtitle?: any;
  children: any;
  closeButton?: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isProcessing?: boolean;
  buttons?: {
    cancel?: { label: string; className?: string };
    confirm?: { label: string; className?: string };
  };
  icon?: {
    className?: string;
    component: any;
  };
};

export default function FormDialog({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  closeButton,
  buttons,
  isProcessing,
  onSubmit,
  icon
}: Props) {
  const { backdrop, positioner, content, title: titleStyle, description, closeTrigger } = dialogStyles();
  const { cancel, confirm } = buttons || {};
  return (
    <ArkDialog.Root
      open={isOpen}
      onOpenChange={onClose}
      closeOnEscape={!isProcessing}
      closeOnInteractOutside={!isProcessing}
    >
      <Portal>
        <ArkDialog.Backdrop className={backdrop} />
        <ArkDialog.Positioner className={positioner}>
          <ArkDialog.Content className={content} asChild>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit(e);
              }}
            >
              <ArkDialog.Title className={titleStyle} asChild>
                <span>
                  {icon && <icon.component className={cx(css({ mr: "0.75rem" }), icon.className)} />} <h4>{title}</h4>
                </span>
              </ArkDialog.Title>
              <ArkDialog.Description className={description} asChild>
                <p>{subtitle}</p>
              </ArkDialog.Description>
              {children}
              {closeButton && (
                <ArkDialog.CloseTrigger className={closeTrigger} disabled={isProcessing}>
                  <Close />
                </ArkDialog.CloseTrigger>
              )}
              {(cancel || confirm) && (
                <Flex mt="1.5rem" columnGap="0.5rem" justifyContent="flex-end">
                  {cancel && (
                    <button
                      disabled={isProcessing}
                      type="button"
                      onClick={onClose}
                      className={cx(button({ variant: "text" }), cancel.className)}
                    >
                      {cancel.label}
                    </button>
                  )}
                  {confirm && (
                    <button
                      disabled={isProcessing}
                      type="submit"
                      className={cx(button({ variant: "text" }), confirm.className)}
                    >
                      {confirm.label}
                    </button>
                  )}
                </Flex>
              )}
            </form>
          </ArkDialog.Content>
        </ArkDialog.Positioner>
      </Portal>
    </ArkDialog.Root>
  );
}
